import React, { ReactElement, useMemo } from 'react';
import isEqual from 'lodash.isequal';
import { useDeviceType } from 'b2b-apps/shop/common/hooks';
import { isEmpty } from 'b2b-apps/shop/common/utils/empty/empty';
import { config } from 'b2b-apps/shop/config';
import { useProductGalleryEffect } from 'b2b-apps/shop/product/hooks';
import { ROUTES } from 'b2b-apps/shop/routes/components/Router';
import { useRouterPush, useUrlCreator } from 'b2b-apps/shop/routes/hooks';
import { getBrandUrlParams } from 'b2b-apps/shop/routes/utils/url';
import {
  EnhancedComponentProps,
  EnhancedComponentType,
  GalleryImages360Type,
  GalleryImagesType,
  GalleryVideosType,
  InjectedProps,
  WrappedComponentType,
} from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const { sku, manufacturerLogo, manufacturerName } = props;
    const deviceType = useDeviceType();
    const gallery = useProductGalleryEffect(sku);
    const pushAction = useRouterPush();
    const urlCreator = useUrlCreator();
    const { isLoaded: isGalleryLoaded, data: galleryData } = gallery;

    const galleryPhotos = useMemo(
      (): GalleryImagesType => {
        if (!isGalleryLoaded || !galleryData || isEmpty(galleryData.images)) {
          return [];
        }

        return galleryData.images.map((img) => ({
          thumbnail: img.sizes.thumbnail,
          preview: img.sizes.preview,
          full: img.sizes.full,
        }));
      },
      [galleryData, isGalleryLoaded],
    );

    const gallery360 = useMemo(
      (): GalleryImages360Type => {
        if (!isGalleryLoaded || !galleryData || isEmpty(galleryData.images360)) {
          return null;
        }

        return galleryData.images360[0]?.sizes || null;
      },
      [galleryData, isGalleryLoaded],
    );

    const galleryVideos = useMemo(
      (): GalleryVideosType => {
        if (!isGalleryLoaded || !galleryData || isEmpty(galleryData.videos)) {
          return null;
        }

        return galleryData.videos;
      },
      [galleryData, isGalleryLoaded],
    );

    const manufacturerLogoUrl = useMemo(
      (): string | null => {
        let logo;

        if (manufacturerLogo.length > 0) {
          logo = manufacturerLogo.find((resolution) => resolution.retina)
            || manufacturerLogo.find((resolution) => !resolution.retina);
        }

        return logo?.href || null;
      },
      [manufacturerLogo],
    );

    const manufacturerLink = useMemo(
      (): string => `${urlCreator(ROUTES.brand)}${getBrandUrlParams(manufacturerName)}`,
      [manufacturerName, urlCreator],
    );

    const handleManufacturerLogoClick = (): void => {
      pushAction(
        `${urlCreator(ROUTES.brand)}${getBrandUrlParams(manufacturerName)}`,
      );
    };

    const injectedProps: InjectedProps = {
      deviceType,
      gallery,
      galleryPhotos,
      gallery360,
      galleryVideos,
      manufacturerLink,
      manufacturerLogoUrl,
      onManufacturerLogoClick: handleManufacturerLogoClick,
      cdnUrl: config.url.cdn.images,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return React.memo(WithContainer, (prevProps, nextProps) => isEqual(prevProps, nextProps));
};

export { withContainer };
